import { useState, useEffect } from "react";
import API from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { ShieldCheck } from "@phosphor-icons/react";
import { DEFAULT_CLICK_CAP, NO_CAP } from "@/lib/trafficRules";

// MaxBounty's own per-campaign channel permissions (Y/N), as returned by
// their API — a DIFFERENT taxonomy than this platform's own
// allowed_traffic_types matrix above, so it's surfaced read-only here
// rather than merged into the editable matrix. Labels mirror the API's
// own field names 1:1.
const NETWORK_CHANNEL_LABELS = {
  contextual_traffic: "Contextual",
  display_traffic: "Display",
  search_traffic: "Search",
  social_media_traffic: "Social media",
  native_traffic: "Native",
  mobile_traffic: "Mobile",
  email_traffic: "Email",
  incentive_traffic: "Incentive",
  brand_bid_traffic: "Brand bidding",
};

function stripHtml(raw) {
  if (!raw) return "";
  return raw.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

// Canonical traffic types — MUST mirror services/traffic_rules.py
// `TRAFFIC_TYPES` + `TRAFFIC_TYPE_DEFAULTS`. If you add one, add it
// there too or the merchant feed will silently drop it.
const TRAFFIC_TYPES = [
  { type: "domain_redirect",    label: "Domain redirect",    defaultAllowed: true },
  { type: "search_syndication", label: "Search syndication", defaultAllowed: true },
  { type: "programmatic_rtb",   label: "Programmatic RTB",   defaultAllowed: true },
  { type: "direct_publisher",   label: "Direct publisher",   defaultAllowed: true },
  { type: "incentivized",       label: "Incentivized",       defaultAllowed: true },
  { type: "toolbar_extension",  label: "Toolbar extension",  defaultAllowed: true },
  { type: "coupon_promo",       label: "Coupon / promo",     defaultAllowed: false },
  { type: "bnpl_financing",     label: "BNPL / financing",   defaultAllowed: false },
  { type: "cashback_loyalty",   label: "Cashback / loyalty", defaultAllowed: false },
  { type: "trademark_bidding",  label: "Trademark bidding",  defaultAllowed: false },
  { type: "forced_clicks",      label: "Forced clicks",      defaultAllowed: false },
];

/** Publisher×brand cap mode — null inherit ≠ explicit -1 no-cap. */
const CAP_MODE_DEFAULT = "default";
const CAP_MODE_NO_CAP = "no_cap";
const CAP_MODE_CUSTOM = "custom";

function defaultMatrix() {
  return TRAFFIC_TYPES.map(({ type, defaultAllowed }) => ({
    type,
    allowed: defaultAllowed,
  }));
}

function mergeMatrix(stored) {
  // Brand may have a partial / older stored value; pad to all 11 using
  // per-type defaults (policy types default false).
  const byType = new Map((stored || []).map((r) => [r.type, !!r.allowed]));
  return TRAFFIC_TYPES.map(({ type, defaultAllowed }) => ({
    type,
    allowed: byType.has(type) ? byType.get(type) : defaultAllowed,
  }));
}

// MaxBounty-specific starting defaults, applied only when the brand has
// never had this matrix explicitly saved (an admin's own saved choice
// always wins — this never overwrites a stored value).
//
// Only two of MaxBounty's 9 channels have a genuine 1:1 match in our own
// 11-type taxonomy (incentive_traffic -> incentivized, brand_bid_traffic
// -> trademark_bidding) — their Email/Search/Social/Contextual/Display/
// Native/Mobile channels describe marketing CHANNELS, ours describes
// traffic ACQUISITION methods (coupon sites, cashback, toolbar extensions,
// forced clicks, etc.), so there's no honest mapping for the rest; they
// stay untouched rather than guessed.
function deriveMaxBountyDefaults(networkTrafficNotes) {
  const overrides = {};
  const nt = networkTrafficNotes?.allowed_traffic_types || {};
  if (String(nt.incentive_traffic || "").toUpperCase() === "N") {
    overrides.incentivized = false;
  }
  if (String(nt.brand_bid_traffic || "").toUpperCase() === "N") {
    overrides.trademark_bidding = false;
  }
  // Independent platform policy for MaxBounty, unrelated to any specific
  // campaign's own stated terms.
  overrides.programmatic_rtb = false;
  return overrides;
}

/**
 * Drawer for editing a brand's traffic rules:
 *   • daily click cap (500 default, -1 = no cap, 0 = blocked)
 *   • allowed traffic types (11 toggles)
 *
 * Two scopes:
 *   • scope="brand"   → PATCH /admin/brands/{campaign_id}/traffic-rules
 *   • scope="publisher" → PUT  /admin/publisher-brands/{publisher_id}/
 *                            {brand_id}/traffic-rules
 *
 * Publisher scope: dropdown distinguishes
 *   • Use publisher default (null) — inherit pub cap
 *   • No cap (-1) — force unlimited for this brand
 *   • Custom number
 * Clear override still POSTs null for both fields.
 */
export default function TrafficRulesDrawer({
  open,
  onClose,
  scope = "brand",      // "brand" | "publisher"
  brand,                 // { campaign_id, name, click_cap_per_day, allowed_traffic_types }
  publisherId = null,    // required when scope === "publisher"
  onSaved,
}) {
  const [cap, setCap] = useState("");
  const [capMode, setCapMode] = useState(CAP_MODE_DEFAULT);
  const [matrix, setMatrix] = useState(defaultMatrix());
  const [saving, setSaving] = useState(false);

  // Sync local form state from props each time the drawer opens or
  // the target brand changes — controlled-form-from-props idiom.
  // (eslint flags setting state in effects via newer plugin rules;
  // the warning is a false positive here since the source of truth
  // IS the props.)
  // eslint-disable-next-line
  useEffect(() => {
    if (!open || !brand) return;
    if (scope === "publisher") {
      const ov = brand.click_cap_per_day_override;
      if (ov === null || ov === undefined) {
        setCapMode(CAP_MODE_DEFAULT);
        setCap("");
      } else if (Number(ov) === NO_CAP) {
        setCapMode(CAP_MODE_NO_CAP);
        setCap("");
      } else {
        setCapMode(CAP_MODE_CUSTOM);
        setCap(String(ov));
      }
    } else {
      const capVal = brand.click_cap_per_day ?? DEFAULT_CLICK_CAP;
      setCap(capVal === NO_CAP || capVal === null || capVal === undefined ? "" : String(capVal));
    }
    const storedMatrix = scope === "publisher"
      ? (brand.allowed_traffic_types_override ?? brand.allowed_traffic_types)
      : brand.allowed_traffic_types;
    let initialMatrix = mergeMatrix(storedMatrix);
    // brand.allowed_traffic_types is NEVER empty by the time it reaches
    // here — the backend's list endpoint (_attach_traffic_effective in
    // admin_brands.py) always fills it with a computed all-allowed
    // default before sending the row, even when nothing was ever saved.
    // traffic_rules_updated_at is the one field that survives that merge
    // untouched, so it's the only reliable "an admin explicitly saved
    // this brand's rules" signal — checked instead of storedMatrix.
    const brandExplicitlyConfigured = Boolean(brand.traffic_rules_updated_at);
    const noOverride = scope !== "publisher" || brand.allowed_traffic_types_override == null;
    if (!brandExplicitlyConfigured && noOverride && (brand.network || "").toLowerCase() === "maxbounty") {
      const overrides = deriveMaxBountyDefaults(brand.network_traffic_notes);
      initialMatrix = initialMatrix.map((row) =>
        row.type in overrides ? { ...row, allowed: overrides[row.type] } : row
      );
    }
    setMatrix(initialMatrix);
  }, [open, brand, scope]);

  const toggleType = (idx) => {
    setMatrix((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, allowed: !r.allowed } : r))
    );
  };

  const parseBrandCap = () => {
    const trimmed = (cap || "").trim();
    if (!trimmed) return NO_CAP;                   // blank → unlimited
    const n = Number.parseInt(trimmed, 10);
    if (Number.isNaN(n)) return null;            // invalid → reject
    return n < 0 ? NO_CAP : n;
  };

  /** Publisher×brand: null inherit | -1 no-cap | ≥0 custom. */
  const parsePublisherOverride = () => {
    if (capMode === CAP_MODE_DEFAULT) return { ok: true, value: null };
    if (capMode === CAP_MODE_NO_CAP) return { ok: true, value: NO_CAP };
    const trimmed = (cap || "").trim();
    if (!trimmed) return { ok: false, error: "Enter a daily click cap number" };
    const n = Number.parseInt(trimmed, 10);
    if (Number.isNaN(n) || n < 0) {
      return { ok: false, error: "Custom cap must be 0 or a positive integer" };
    }
    return { ok: true, value: n };
  };

  const save = async () => {
    setSaving(true);
    try {
      if (scope === "brand") {
        const capVal = parseBrandCap();
        if (capVal === null) {
          toast.error("Daily click cap must be a number, blank, or -1 for no cap");
          setSaving(false);
          return;
        }
        await API.patch(`/admin/brands/${brand.campaign_id}/traffic-rules`, {
          click_cap_per_day: capVal,
          allowed_traffic_types: matrix,
        });
        toast.success(`Traffic rules updated for ${brand.name}`);
      } else {
        const parsed = parsePublisherOverride();
        if (!parsed.ok) {
          toast.error(parsed.error);
          setSaving(false);
          return;
        }
        await API.put(
          `/admin/publisher-brands/${publisherId}/${brand.campaign_id || brand.brand_id}/traffic-rules`,
          {
            click_cap_per_day_override: parsed.value,
            allowed_traffic_types_override: matrix,
          }
        );
        toast.success(
          parsed.value === null
            ? "Override cleared — falls back to publisher default"
            : parsed.value === NO_CAP
              ? "No-cap override saved for this brand"
              : "Publisher × brand click cap saved"
        );
      }
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const clearOverride = async () => {
    if (scope !== "publisher") return;
    setSaving(true);
    try {
      await API.put(
        `/admin/publisher-brands/${publisherId}/${brand.campaign_id || brand.brand_id}/traffic-rules`,
        {
          click_cap_per_day_override: null,
          allowed_traffic_types_override: null,
        }
      );
      toast.success("Override cleared — falls back to publisher default");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent
        className="sm:max-w-md flex flex-col p-0 gap-0 overflow-hidden"
        data-testid="traffic-rules-drawer"
      >
        <SheetHeader className="px-6 pt-6 pb-3 shrink-0 pr-12">
          <SheetTitle className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-emerald-600" />
            Traffic Rules
          </SheetTitle>
          <SheetDescription className="text-xs">
            {scope === "brand"
              ? <>Brand-level default — applies to every publisher unless overridden.</>
              : <>
                  Per-publisher × brand override.{" "}
                  <span className="font-medium">No cap</span> stores{" "}
                  <span className="font-mono">-1</span> (unlimited for this brand).{" "}
                  <span className="font-medium">Use publisher default</span> clears
                  the override (null). Brand-level caps still act as a ceiling when
                  tighter.
                </>}
            {brand?.name ? <span className="block mt-1 font-medium text-slate-700">{brand.name}</span> : null}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-6">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-bold block mb-1.5">
              Daily click cap
            </label>
            {scope === "publisher" ? (
              <div className="space-y-2">
                <Select
                  value={capMode}
                  onValueChange={(v) => {
                    setCapMode(v);
                    if (v !== CAP_MODE_CUSTOM) setCap("");
                  }}
                >
                  <SelectTrigger
                    className="h-9"
                    data-testid="traffic-rules-cap-mode"
                  >
                    <SelectValue placeholder="Select cap mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={CAP_MODE_DEFAULT}>
                      Use publisher default
                    </SelectItem>
                    <SelectItem value={CAP_MODE_NO_CAP}>
                      No cap (-1)
                    </SelectItem>
                    <SelectItem value={CAP_MODE_CUSTOM}>
                      Custom number
                    </SelectItem>
                  </SelectContent>
                </Select>
                {capMode === CAP_MODE_CUSTOM ? (
                  <Input
                    type="number"
                    min={0}
                    placeholder="e.g. 500"
                    value={cap}
                    onChange={(e) => setCap(e.target.value)}
                    data-testid="traffic-rules-cap-input"
                    className="h-9 font-mono"
                  />
                ) : null}
                <p className="text-[11px] text-slate-400 leading-snug">
                  <span className="font-medium">Use publisher default</span> → null
                  (inherit). <span className="font-medium">No cap</span> → stores{" "}
                  <span className="font-mono">-1</span> (unlimited, not empty).{" "}
                  <span className="font-medium">0</span> in custom → block all traffic.
                </p>
              </div>
            ) : (
              <>
                <Input
                  type="number"
                  min={-1}
                  placeholder="-1 (default = no cap)"
                  value={cap}
                  onChange={(e) => setCap(e.target.value)}
                  data-testid="traffic-rules-cap-input"
                  className="h-9 font-mono"
                />
                <p className="text-[11px] text-slate-400 mt-1.5 leading-snug">
                  Per brand, per day. Default when unset is{" "}
                  <span className="font-medium">no cap</span>.{" "}
                  <span className="font-medium">-1</span> or blank → no cap.{" "}
                  <span className="font-medium">0</span> → block all traffic.{" "}
                  <span className="font-medium">Test clicks are NOT excluded</span> from the count.
                </p>
              </>
            )}
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-bold block mb-2">
              Allowed traffic types
            </label>
            <div className="space-y-2">
              {brand?.network_traffic_notes &&
                Object.entries(brand.network_traffic_notes.allowed_traffic_types || {}).map(
                  ([key, val]) => {
                    const allowed = String(val).toUpperCase() === "Y";
                    return (
                      <div
                        key={key}
                        className="flex items-center justify-between px-3 py-2 border border-slate-200 rounded-md bg-white"
                        title={
                          (allowed ? "Allowed by MaxBounty" : "Not allowed by MaxBounty") +
                          " — read-only, not one of this platform's enforced types"
                        }
                      >
                        <span className={`text-sm font-mono ${allowed ? "text-slate-700" : "text-red-700"}`}>
                          {NETWORK_CHANNEL_LABELS[key] || key}
                        </span>
                        {/* MaxBounty's own terms — display only, never fires
                            onCheckedChange or writes anywhere. */}
                        <Switch checked={allowed} disabled />
                      </div>
                    );
                  }
                )}
              {matrix.map((row, i) => (
                <div
                  key={row.type}
                  className="flex items-center justify-between px-3 py-2 border border-slate-200 rounded-md bg-white"
                  data-testid={`traffic-type-${row.type}`}
                >
                  <span className="text-sm text-slate-700 font-mono">
                    {TRAFFIC_TYPES.find((t) => t.type === row.type)?.label || row.type}
                  </span>
                  <Switch
                    checked={row.allowed}
                    onCheckedChange={() => toggleType(i)}
                    data-testid={`traffic-type-${row.type}-switch`}
                  />
                </div>
              ))}
            </div>
            {brand?.network_traffic_notes &&
              (stripHtml(brand.network_traffic_notes.rules) ||
                stripHtml(brand.network_traffic_notes.search_restrictions)) && (
              <p className="text-[11px] text-slate-400 leading-snug mt-2">
                {stripHtml(brand.network_traffic_notes.rules)}
                {stripHtml(brand.network_traffic_notes.search_restrictions) && (
                  <>
                    {" "}
                    <span className="font-medium">Search:</span>{" "}
                    {stripHtml(brand.network_traffic_notes.search_restrictions)}
                  </>
                )}
              </p>
            )}
          </div>
        </div>

        <SheetFooter className="px-6 py-3 border-t border-slate-200 flex flex-row justify-between gap-2 shrink-0 bg-white">
          {scope === "publisher" ? (
            <Button
              variant="outline"
              size="sm"
              onClick={clearOverride}
              disabled={saving}
              className="text-xs text-slate-500 hover:text-red-600"
              data-testid="traffic-rules-clear-override"
            >
              Clear override
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              disabled={saving}
              data-testid="traffic-rules-cancel"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={save}
              disabled={saving}
              data-testid="traffic-rules-save"
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
